import React, { useEffect, useState } from "react";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";

import useEvent from "@/hooks/useEvent";

interface Option {
  label: string;
  value: string;
}

interface MobileSelectProps {
  title: string;
  options: Option[];
  selectOptions: Option[];
  open: boolean;
  onOpen: (value: boolean) => void;
  onChange: (data: Option[]) => void;
}

const MobileSelect = ({
  title,
  open,
  onOpen,
  onChange,
  options,
  selectOptions,
}: MobileSelectProps) => {
  const [data, setData] = useState<Option[]>([]);
  const [isReset, setIsReset] = useState(false);

  const handleClose = useEvent(() => {
    onOpen(false);
  });

  const handleOk = useEvent(() => {
    onChange(isReset ? [] : data);
    onOpen(false);
  });

  useEffect(() => {
    if (selectOptions?.length === 1 && !selectOptions[0].value) {
      setData(options);
    } else {
      setData(selectOptions);
    }
  }, [selectOptions, options]);

  useEffect(() => {
    if (open) {
      setIsReset(false);
    }
  }, [open]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      PaperProps={{ style: { width: "100%", maxHeight: "80vh", borderRadius: "12px 12px 0 0" } }}
    >
      <div className="h-full overflow-hidden flex flex-col">
        <DialogTitle>
          <Typography color="textPrimary">{title}</Typography>
        </DialogTitle>

        <IconButton
          onClick={handleClose}
          className="!absolute !right-[8px] !top-[8px]"
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent dividers className="!p-[0px] overflow-auto">
          {options.map((option, i) => {
            const selected = !!data.find((ele) => ele.value === option.value);
            const isLast = i === options.length - 1;

            return (
              <div key={option.value} className="w-full">
                <div
                  className="flex items-center p-[3px]"
                  onClick={(e) => {
                    e.stopPropagation();

                    if (selected) {
                      setData(data.filter((ele) => ele.value !== option.value));
                    } else {
                      setData([...data, option]);
                    }

                    setIsReset(false);
                  }}
                >
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxIcon />}
                    checked={selected}
                  />

                  <div className="flex flex-1">
                    <Typography color={"textPrimary"} variant="body2">
                      {option.label}
                    </Typography>
                  </div>

                  <Button
                    variant="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      setData([option]);
                      setIsReset(false);
                    }}
                  >
                    only
                  </Button>
                </div>
                <Box
                  className="mx-[15px]"
                  sx={{
                    height: isLast ? 0 : "1px",
                    backgroundColor: (theme) => theme.palette.divider,
                  }}
                />
              </div>
            );
          })}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setIsReset(data.length !== options.length);
              setData(data.length === options.length ? [] : options);
            }}
          >
            {data.length === options.length ? "Deselect All" : "Reset"}
          </Button>
          <Button onClick={handleOk}>Apply</Button>
        </DialogActions>
      </div>
    </Drawer>
  );
};

export default React.memo(MobileSelect);
